import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";
import { createPasskey, describePasskeyRegistrationError } from "./orendaWebAuthn";
import { parseStartAddPasskeyResponse } from "./orendaApplicationHelpers";

// Walks a user through enrolling a passkey against their Orenda account.
// Required once (per device) before they can view secure card details.
//
// Flow (as documented at
//   https://api-docs.orenda-apps.com/api-reference/passkeys/start-adding-a-passkey/):
//   1. intro             — explain + "Set up" button
//   2. requesting-email  — call startAddPasskey() with no code
//   3. email-code        — user enters 6-digit code we emailed them
//   4. verifying         — call startAddPasskey() again with code
//   5. waiting-device    — navigator.credentials.create() prompts Face ID etc.
//   6. finishing         — call finishAddPasskey() with the attestation
//   7. success           — done, user can now reveal card details
//
// Non-SSO users may skip the email step: startAddPasskey() returns the
// publicKey directly on the first call. We detect that and jump to step 5.

const OrendaPasskeyEnroll = ({ open, onClose, onEnrolled }) => {
  const [phase, setPhase] = useState("intro");
  const [friendlyName, setFriendlyName] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [error, setError] = useState(null);
  const sessionTokenRef = useRef(null);
  const publicKeyRef = useRef(null);

  const reset = useCallback(() => {
    setPhase("intro");
    setFriendlyName("");
    setEmailCode("");
    setError(null);
    sessionTokenRef.current = null;
    publicKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Default the friendly_name to something sensible on mount.
  useEffect(() => {
    if (friendlyName) return;
    try {
      const platform = navigator.userAgent.includes("Mac")
        ? "Mac"
        : navigator.userAgent.includes("iPhone")
        ? "iPhone"
        : navigator.userAgent.includes("Android")
        ? "Android"
        : navigator.userAgent.includes("Windows")
        ? "Windows"
        : "This device";
      setFriendlyName(`My ${platform}`);
    } catch {
      setFriendlyName("My device");
    }
  }, [friendlyName]);

  const startEnrollment = useCallback(async () => {
    setError(null);
    setPhase("requesting-email");
    try {
      const resp = await orendaService.startAddPasskeyOrenda({ friendlyName });
      const flow = parseStartAddPasskeyResponse(resp);
      if (flow.needsEmail) {
        setPhase("email-code");
        return;
      }
      if (flow.publicKey) {
        // Non-SSO path — jump straight to device prompt.
        sessionTokenRef.current = flow.sessionToken;
        publicKeyRef.current = flow.publicKey;
        await promptAndFinish(flow.sessionToken, flow.publicKey);
      } else {
        throw new Error("Unexpected server response — missing both challenge and publicKey.");
      }
    } catch (err) {
      setError(humanizeError(err));
      setPhase("intro");
    }
  }, [friendlyName]);

  const submitEmailCode = useCallback(async () => {
    if (!/^\d{4,8}$/.test(emailCode.trim())) {
      setError("Enter the code from your email (4–8 digits).");
      return;
    }
    setError(null);
    setPhase("verifying");
    try {
      const resp = await orendaService.startAddPasskeyOrenda({
        friendlyName,
        emailCode: emailCode.trim(),
      });
      const flow = parseStartAddPasskeyResponse(resp);
      if (!flow.publicKey || !flow.sessionToken) {
        throw new Error("That didn't work. The code may be wrong or expired.");
      }
      sessionTokenRef.current = flow.sessionToken;
      publicKeyRef.current = flow.publicKey;
      await promptAndFinish(flow.sessionToken, flow.publicKey);
    } catch (err) {
      setError(humanizeError(err));
      setPhase("email-code");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailCode, friendlyName]);

  const promptAndFinish = async (sessionToken, publicKey) => {
    setPhase("waiting-device");
    let attestation;
    try {
      attestation = await createPasskey(publicKey);
    } catch (err) {
      setError(describePasskeyRegistrationError(err));
      setPhase("intro");
      return;
    }
    setPhase("finishing");
    try {
      await orendaService.finishAddPasskeyOrenda({
        sessionToken,
        credential: attestation,
      });
      setPhase("success");
      // Small delay so the user sees the success state before parent
      // reacts (e.g. by advancing them to the card-reveal flow).
      window.setTimeout(() => onEnrolled?.(), 900);
    } catch (err) {
      setError(humanizeError(err));
      setPhase("intro");
    }
  };

  if (!open) return null;

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-enroll-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="orenda-reveal">
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">One-time setup</div>
            <h2 id="orenda-enroll-title" className="orenda-reveal-title">
              Secure your card
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="orenda-reveal-body">
          {phase === "intro" && (
            <IntroStep
              friendlyName={friendlyName}
              setFriendlyName={setFriendlyName}
              onStart={startEnrollment}
              error={error}
            />
          )}

          {phase === "requesting-email" && (
            <Spinner label="Sending you a verification code…" />
          )}

          {phase === "email-code" && (
            <EmailCodeStep
              code={emailCode}
              setCode={setEmailCode}
              onSubmit={submitEmailCode}
              onResend={startEnrollment}
              error={error}
            />
          )}

          {phase === "verifying" && (
            <Spinner label="Checking your code…" />
          )}

          {phase === "waiting-device" && (
            <Spinner label="Confirm on your device to finish setup…" />
          )}

          {phase === "finishing" && (
            <Spinner label="Saving your passkey…" />
          )}

          {phase === "success" && <SuccessStep />}
        </div>
      </div>
    </div>
  );
};

// ─── Step UIs ──────────────────────────────────────────────────────

const IntroStep = ({ friendlyName, setFriendlyName, onStart, error }) => (
  <>
    <p className="orenda-enroll-copy">
      To see your full card number, we need to set up a passkey — a
      biometric or PIN confirmation on this device. You'll do this once;
      future reveals take seconds.
    </p>

    <div className="orenda-enroll-field">
      <label htmlFor="orenda-enroll-name">Name this device</label>
      <input
        id="orenda-enroll-name"
        type="text"
        value={friendlyName}
        onChange={(e) => setFriendlyName(e.target.value.slice(0, 40))}
        placeholder="e.g. My laptop"
        maxLength={40}
      />
      <div className="orenda-enroll-hint">
        Shown in your passkey list so you can tell devices apart later.
      </div>
    </div>

    {error && <div className="orenda-reveal-countdown orenda-enroll-err">{error}</div>}

    <div className="orenda-enroll-actions">
      <button type="button" className="orenda-btn-primary" onClick={onStart}>
        Set up passkey
      </button>
    </div>
  </>
);

const EmailCodeStep = ({ code, setCode, onSubmit, onResend, error }) => (
  <>
    <p className="orenda-enroll-copy">
      We sent a 6-digit code to your email. Enter it to confirm it's you,
      then we'll ask your device to set up the passkey.
    </p>

    <div className="orenda-enroll-field">
      <label htmlFor="orenda-enroll-code">Verification code</label>
      <input
        id="orenda-enroll-code"
        type="text"
        inputMode="numeric"
        pattern="\d*"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
        placeholder="123456"
        autoFocus
        autoComplete="one-time-code"
      />
    </div>

    {error && <div className="orenda-reveal-countdown orenda-enroll-err">{error}</div>}

    <div className="orenda-enroll-actions">
      <button
        type="button"
        className="orenda-btn-ghost"
        onClick={onResend}
      >
        Resend code
      </button>
      <button
        type="button"
        className="orenda-btn-primary"
        onClick={onSubmit}
      >
        Continue
      </button>
    </div>
  </>
);

const SuccessStep = () => (
  <div className="orenda-reveal-expired" style={{ padding: "28px 10px 20px" }}>
    <div
      aria-hidden
      style={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "#e8f3eb",
        color: "#2f6b3a",
        margin: "0 auto 12px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 28,
      }}
    >
      ✓
    </div>
    <h3 className="orenda-reveal-expired-title">You're set up</h3>
    <p className="orenda-reveal-expired-sub">
      Opening your card details now…
    </p>
  </div>
);

const Spinner = ({ label }) => (
  <div className="orenda-reveal-spinner">
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-label">{label}</div>
  </div>
);

// ─── helpers ───────────────────────────────────────────────────────

const humanizeError = (err) => {
  if (err?.code === "SSO_SESSION_EXPIRED") {
    return "Your session expired. Please sign in again and try again.";
  }
  return (
    err?.response?.data?.errorMessage ||
    err?.message ||
    "Something went wrong — please try again."
  );
};

export default OrendaPasskeyEnroll;
